import { createClient } from '@supabase/supabase-js';
import { countWords, draftKey, goalKey, listDrafts, readVersions, saveDraft, versionsKey } from './writer-data.mjs';

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const redirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL;
const client = url && key ? createClient(url, key) : null;

export function setupCloud({ day, editor, goalInput, saveStatus, render }) {
  const button = document.querySelector('#account-button');
  const dialog = document.querySelector('#account-dialog');
  const emailInput = document.querySelector('#account-email');
  const sendButton = document.querySelector('#account-send');
  const signOutButton = document.querySelector('#account-signout');
  const message = document.querySelector('#account-message');
  const identity = document.querySelector('#account-identity');
  const conflictsList = document.querySelector('#cloud-conflicts');
  let user = null;
  let timer = null;
  let pending = null;
  let sending = false;
  const remote = new Map();
  const conflicts = new Map();

  const say = (text) => { message.textContent = text; };
  const setIdentity = () => {
    button.textContent = user ? 'Account' : 'Cloud backup';
    identity.textContent = user ? `Signed in as ${user.email}` : 'Sign in to save a private cloud copy. Writing and downloadable backups remain free without an account.';
    signOutButton.hidden = !user;
    sendButton.hidden = !!user;
    emailInput.hidden = !!user;
  };
  const showConflicts = () => {
    conflictsList.replaceChildren();
    button.textContent = conflicts.size ? `Account (${conflicts.size} to review)` : user ? 'Account' : 'Cloud backup';
    for (const [date, row] of conflicts) {
      const line = document.createElement('div');
      line.className = 'cloud-conflict';
      const label = document.createElement('span');
      label.textContent = `${date}: browser and cloud copies differ`;
      const download = document.createElement('button');
      download.type = 'button';
      download.textContent = 'Download cloud copy';
      download.addEventListener('click', () => {
        const link = document.createElement('a');
        const blob = new Blob([row.draft], { type: 'text/plain;charset=utf-8' });
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = `climbwriter-cloud-${date}.txt`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      });
      const useCloud = document.createElement('button');
      useCloud.type = 'button';
      useCloud.textContent = 'Use cloud copy here';
      useCloud.addEventListener('click', () => {
        try {
          const local = localStorage.getItem(draftKey(date));
          if (local !== null && local !== row.draft) {
            const versions = readVersions(localStorage, date);
            versions.unshift({ content: local, savedAt: Date.now() });
            localStorage.setItem(versionsKey(date), JSON.stringify(versions.slice(0, 30)));
          }
          localStorage.setItem(draftKey(date), row.draft);
          remote.set(date, row);
          conflicts.delete(date);
          if (date === day) { editor.value = row.draft; render(); }
          showConflicts();
          say('Cloud copy loaded. Your previous browser draft is in Recovery points.');
        } catch {
          say('Browser storage is unavailable. Your cloud copy was not changed.');
        }
      });
      line.append(label, download, useCloud);
      conflictsList.append(line);
    }
    if (conflicts.size) {
      say(`${conflicts.size} differing day(s) need review. Neither copy was overwritten.`);
      saveStatus.textContent = 'Saved in browser. Review the different cloud copy in Account.';
    }
  };

  async function readCloudDays() {
    const rows = [];
    for (let offset = 0; ; offset += 1000) {
      const { data, error } = await client.from('writing_days')
        .select('day,draft,words_written,daily_goal,updated_at')
        .eq('user_id', user.id).order('day').range(offset, offset + 999);
      if (error) throw error;
      rows.push(...data);
      if (data.length < 1000) return rows;
    }
  }

  async function mergeCloudDays() {
    if (!client || !user) return;
    const currentUser = user.id;
    const cloudRows = await readCloudDays();
    if (user?.id !== currentUser) return;
    remote.clear();
    conflicts.clear();
    const cloudDays = new Set();
    let downloaded = 0;
    for (const row of cloudRows) {
      cloudDays.add(row.day);
      const local = localStorage.getItem(draftKey(row.day));
      if (local === null && row.day === day && editor.value !== '') {
        saveDraft(localStorage, day, editor.value);
        conflicts.set(day, row);
      } else if (local === null) {
        localStorage.setItem(draftKey(row.day), row.draft);
        remote.set(row.day, row);
        downloaded += 1;
        if (row.day === day && editor.value === '') { editor.value = row.draft; render(); }
      } else if (local === row.draft) {
        remote.set(row.day, row);
      } else {
        conflicts.set(row.day, row);
      }
    }
    const drafts = listDrafts(localStorage);
    const newRows = Object.entries(drafts)
      .filter(([date]) => !cloudDays.has(date))
      .map(([date, draft]) => ({
        user_id: currentUser, day: date, draft, words_written: countWords(draft),
        daily_goal: Number(localStorage.getItem(goalKey)) || 500,
      }));
    if (newRows.length) {
      const { error } = await client.from('writing_days')
        .upsert(newRows, { onConflict: 'user_id,day', ignoreDuplicates: true });
      if (error) throw error;
      // Read the server timestamps, including rows inserted by another session meanwhile.
      for (const row of await readCloudDays()) {
        if (!remote.has(row.day) && !conflicts.has(row.day)) {
          if (localStorage.getItem(draftKey(row.day)) === row.draft) remote.set(row.day, row);
          else conflicts.set(row.day, row);
        }
      }
    }
    showConflicts();
    if (!conflicts.size) say(`Cloud backup ready. ${downloaded} day(s) restored here; ${newRows.length} local day(s) uploaded.`);
  }

  async function sendCloudCopy(date, draft, goal) {
    if (!client || !user || conflicts.has(date)) return;
    const previous = remote.get(date);
    if (!previous) {
      const { data, error } = await client.from('writing_days')
        .insert({ user_id: user.id, day: date, draft, words_written: countWords(draft), daily_goal: goal })
        .select('day,draft,words_written,daily_goal,updated_at').single();
      if (error) throw error;
      remote.set(date, data);
      return;
    }
    if (previous.draft === draft && previous.daily_goal === goal) return;
    const { data, error } = await client.from('writing_days')
      .update({ draft, words_written: countWords(draft), daily_goal: goal, updated_at: new Date().toISOString() })
      .eq('user_id', user.id).eq('day', date).eq('updated_at', previous.updated_at)
      .select('day,draft,words_written,daily_goal,updated_at').maybeSingle();
    if (error) throw error;
    if (!data) {
      const { data: current, error: readError } = await client.from('writing_days')
        .select('day,draft,words_written,daily_goal,updated_at')
        .eq('user_id', user.id).eq('day', date).single();
      if (readError) throw readError;
      conflicts.set(date, current);
      showConflicts();
      return;
    }
    remote.set(date, data);
  }

  async function flush() {
    if (sending || !pending || !user) return;
    sending = true;
    while (pending && user) {
      const next = pending;
      pending = null;
      try {
        await sendCloudCopy(next.date, next.draft, next.goal);
        if (!conflicts.has(next.date)) saveStatus.textContent = 'Saved in browser and cloud';
      } catch {
        saveStatus.textContent = 'Saved in browser. Cloud backup could not be updated; retry by editing again.';
      }
    }
    sending = false;
  }
  function schedule(date, draft, goal) {
    if (!user || conflicts.has(date)) return;
    pending = { date, draft, goal };
    window.clearTimeout(timer);
    timer = window.setTimeout(flush, 1100);
  }

  button.addEventListener('click', () => dialog.showModal());
  dialog.querySelector('[data-close-dialog]').addEventListener('click', () => dialog.close());
  sendButton.addEventListener('click', async () => {
    if (!client) return;
    const email = emailInput.value.trim();
    if (!emailInput.checkValidity()) { emailInput.reportValidity(); return; }
    sendButton.disabled = true;
    say('Sending your sign-in code…');
    const { error } = await client.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectUrl } });
    sendButton.disabled = false;
    say(error ? error.message : 'Check your email for the sign-in link. It returns you to this page.');
  });
  signOutButton.addEventListener('click', async () => {
    if (!client) return;
    window.clearTimeout(timer);
    pending = null;
    const { error } = await client.auth.signOut();
    if (error) { say(error.message); return; }
    user = null;
    remote.clear();
    conflicts.clear();
    showConflicts();
    setIdentity();
    say('Signed out. Drafts in this browser remain here.');
  });
  setIdentity();
  if (!client || !redirectUrl) {
    button.disabled = true;
    button.title = 'Cloud backup needs a configured site address.';
    say('Cloud backup needs a configured site address.');
  } else {
    client.auth.getUser().then(async ({ data, error }) => {
      if (error || !data.user) return;
      user = data.user;
      setIdentity();
      try { await mergeCloudDays(); }
      catch { say('Could not load cloud backup. Browser drafts remain available.'); }
    });
  }
  return { schedule };
}
